import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type ReactNode
} from 'react';
import bg1 from '@/assets/img/icons/spot-illustrations/corner-1.png';
import bg2 from '@/assets/img/icons/spot-illustrations/corner-2.png';
import bg3 from '@/assets/img/icons/spot-illustrations/corner-3.png';
import bg4 from '@/assets/img/icons/spot-illustrations/corner-4.png';
import AgGridWrapper from '@/app/common/components/ag-grid/AgGridWrapper';
import EstimateClickChart from '../components/EstimateClickChart';
import AgGridSkeleton from '@/app/common/components/skeleton/AgGridSkeleton';
import DataLoadError from '@/components/errors/DataLoadError';
import { getActiveQueryError } from '@/app/common/utils/apiErrorUtils';
import NoKeywordPlannerData from '../components/keyword-planner/NoKeywordPlannerData';
import TextLoading from '@/app/common/components/skeleton/TextLoading';
import FiltersComponent from '@/app/common/components/filters/FiltersComponent';
import { Col, Row } from 'react-bootstrap';
import { plannerDetailsColumnConfig } from '@/app/KeywordResearch/config/plannerDetailsColumnConfig';
import { useLocation, useOutletContext, useParams } from 'react-router-dom';
import { useGetUserInfoQuery } from '@/app/common/api/authApi';
import {
  useGetKeywordsPlannerFuncMutation,
  useGetKeywordsPlannerQuery,
  useExportTableMutation,
  useClusterPlannerFromTaskMutation
} from '@/app/KeywordResearch/api/keywordPlannerApi';
import { colorByIndex } from '@/helpers/utils';
import { FaDesktop, FaMobileScreenButton } from 'react-icons/fa6';
import StatisticsCard from '../components/StatisticsCard';
import CardBarChartLoading from '@/app/common/components/skeleton/CardBarChartLoading';
import FiltersComponentSkeleton from '../../common/components/filters/FiltersComponentSkeleton';
import { intentMap, purposeMap } from '../config/keywordGapColumnConfig';
import PlannerProgressBar from '../components/keyword-planner/PlannerProgressBar';
import ClusterFromTaskButton from '@/app/KeywordResearch/components/ClusterFromTaskButton';

type BreadcrumbItem = { text: string; href: string };

type OutletContext = [BreadcrumbItem[], (items: BreadcrumbItem[]) => void];

type StatItem = {
  title: ReactNode;
  value: number | undefined;
  image: string;
  className?: string;
};

type AgGridWrapperProps = {
  table_name?: string;
  height: string;
  columns: unknown[];
  data?: unknown[];
  total?: number;
  fetchFunction?: unknown;
  additionalData?: Record<string, unknown>;
  settings?: Record<string, unknown>;
};

type EstimateClickChartProps = {
  data: unknown[];
  viewChartMode: string;
  setViewChartMode: (mode: string) => void;
};

const TypedAgGridWrapper = AgGridWrapper as ComponentType<AgGridWrapperProps>;
const TypedEstimateClickChart =
  EstimateClickChart as ComponentType<EstimateClickChartProps>;

/** هم‌تراز با TextLoading و فاصله g-3 بین دو ردیف کارت آمار */
const STAT_CARD_HEIGHT = 100;
const STATS_SECTION_HEIGHT = STAT_CARD_HEIGHT * 2 + 12;

/** /keyword-research/planner/:domain_slug/:domain/:taskId/... */
const getPlannerTaskIdFromPath = (pathname: string): string => {
  const parts = pathname.split('/').filter(Boolean);
  if (parts[0] !== 'keyword-research' || parts[1] !== 'planner') return '';
  return parts[4] ?? '';
};

const PlannerDetails = () => {
  const gridRef = useRef<HTMLDivElement>(null);
  const { slug: slugParam = '' } = useParams<{ slug: string }>();
  const [, setBreadcrumbData] = useOutletContext<OutletContext>();
  const [viewChartMode, setViewChartMode] = useState('desktop');
  const { data: userInfo, isLoading: isUserInfoLoading } = useGetUserInfoQuery();
  const [getPlannerKeywords] = useGetKeywordsPlannerFuncMutation();
  const [exportTable] = useExportTableMutation();
  const [clusterPlannerFromTask, { isLoading: isClustering }] =
    useClusterPlannerFromTaskMutation();
  const { pathname } = useLocation();

  const slug = slugParam || getPlannerTaskIdFromPath(pathname);

  const requestedUser = useMemo(
    () => ({
      name_family: `${userInfo?.data?.first_name ?? ''} ${userInfo?.data?.last_name ?? ''}`.trim(),
      email: userInfo?.data?.email ?? ''
    }),
    [
      userInfo?.data?.first_name,
      userInfo?.data?.last_name,
      userInfo?.data?.email
    ]
  );

  const { data, isError, isLoading, isFetching, error, refetch } =
    useGetKeywordsPlannerQuery(
    {
      page: 1,
      perPage: 50,
      task_id: slug,
      requested_user: requestedUser
    },
    {
      // Only wait for task id + user email; keyword in the URL is unused by this API.
      skip: !slug || isUserInfoLoading,
      refetchOnFocus: false,
      refetchOnReconnect: false,
      refetchOnMountOrArgChange: true
    }
  );

  const isProcessing = data?.data?.status === 'processing';
  const hasNoKeywordData =
    !isProcessing &&
    data?.data?.total === 0 &&
    !data?.data?.rows?.length;

  useEffect(() => {
    setBreadcrumbData([{ text: 'کیورد پلنر', href: '#' }]);
  }, [setBreadcrumbData]);

  const queryError = getActiveQueryError([
    { isError, error, refetch, isFetching },
  ]);
  if (queryError) return <DataLoadError {...queryError} />;
  const taskId = slug || data?.data?.task_id;
  const showContentLoading = isLoading || isProcessing;

  const statItems: StatItem[] = [
    {
      title: 'تعداد کلمات',
      value: data?.data?.num_of_keywords,
      image: bg1
    },
    {
      title: 'میزان جستجو',
      value: data?.data?.sum_of_search_volumes,
      image: bg2
    },
    {
      title: (
        <div>
          تخمین کلیک رتبه یک <FaDesktop />
        </div>
      ),
      value: data?.data?.ctr_rank_one_desktop,
      image: bg3
    },
    {
      title: (
        <div>
          تخمین کلیک رتبه یک <FaMobileScreenButton />
        </div>
      ),
      value: data?.data?.ctr_rank_one_mobile,
      image: bg4
    }
  ];

  const chartData =
    data?.data?.[
      viewChartMode === 'mobile' ? 'ctr_mobile' : 'ctr_desktop'
    ]?.map((num, index) => ({
      name: index === 10 ? 'سایر' : `رتبه ${index + 1}`,
      type: 'bar',
      stack: 'total',
      label: { show: false },
      emphasis: { focus: 'series' },
      data: [num],
      itemStyle: { color: colorByIndex(index) }
    })) ?? [];

  return (
    <div>
      {isProcessing && taskId && (
        <Row>
          <Col xxl={12}>
            <PlannerProgressBar taskId={taskId} active={isProcessing} />
          </Col>
        </Row>
      )}
      <Row>
        <Col xs={12} lg={5}>
          <Row className="g-3 mb-3">
            {statItems.map(({ title, value, image }, index) => {
              const showKeywordCountWhileProcessing =
                isProcessing && index === 0;
              const showStat =
                !showContentLoading || showKeywordCountWhileProcessing;

              return (
                <Col key={index} xs={6} lg={6}>
                  {showStat ? (
                    <StatisticsCard
                      stat={{
                        title,
                        value,
                        decimal: false,
                        compact: true,
                        image,
                        className: 'py-1'
                      }}
                    />
                  ) : (
                    <TextLoading height={STAT_CARD_HEIGHT} />
                  )}
                </Col>
              );
            })}
          </Row>
        </Col>
        <Col xs={12} lg={7}>
          {!showContentLoading ? (
            <TypedEstimateClickChart
              setViewChartMode={setViewChartMode}
              viewChartMode={viewChartMode}
              data={chartData}
            />
          ) : (
            <div
              className="mb-3"
              style={{ height: STATS_SECTION_HEIGHT }}
            >
              <CardBarChartLoading
                className="h-100"
                fillHeight
                title="نموار تخمین کلیک بر اساس رتبه"
                limit={24}
                width={8}
                height={4}
                margin={0}
                marginBetweenLine={4}
                showFooter={false}
              />
            </div>
          )}
        </Col>
      </Row>
      <Row>
        {!isFetching && data?.data ? (
          hasNoKeywordData ? (
            <Col xxl={12}>
              <NoKeywordPlannerData variant="emptyResults" />
            </Col>
          ) : (
            <>
              <Col xxl={12}>
                <FiltersComponent
                  selectItems={{
                    purpose: Object.entries(purposeMap).map(([k, v]) => ({
                      label: v,
                      value: k
                    })),
                    intent: Object.entries(intentMap).map(([k]) => ({
                      label: k,
                      value: k
                    }))
                  }}
                  columns={plannerDetailsColumnConfig(pathname, {
                    location_code: data.data.location_code,
                    language_code: data.data.language_code
                  })}
                />
              </Col>
              <Col>
                <div className="d-flex align-items-center justify-content-between gap-2 mb-2">
                  <ClusterFromTaskButton
                    taskId={taskId}
                    keywordCount={data.data.num_of_keywords ?? data.data.total}
                    mutate={clusterPlannerFromTask}
                    isClustering={isClustering}
                    isTaskReady={!isProcessing}
                  />
                </div>
                <div ref={gridRef}>
                  <TypedAgGridWrapper
                    table_name={pathname.split('/').slice(2, 3).join('/')}
                    settings={{
                      pagination: true,
                      fullscreen: false,
                      changeHeight: false,
                      resetGrid: false,
                      saveGrid: false,
                      export: {
                        enabled: true,
                        fetchFunction: exportTable,
                        additionalData: { task_id: slug }
                      }
                    }}
                    height="746px"
                    columns={plannerDetailsColumnConfig(pathname, {
                      location_code: data.data.location_code,
                      language_code: data.data.language_code
                    })}
                    total={data.data.total}
                    additionalData={{
                      task_id: slug,
                      requested_user: requestedUser
                    }}
                    fetchFunction={getPlannerKeywords}
                    data={data.data.rows}
                  />
                </div>
              </Col>
            </>
          )
        ) : (
          <>
            <Col xxl={12} className="mb-2">
              <FiltersComponentSkeleton />
            </Col>
            <Col xxl={12}>
              <AgGridSkeleton
                height="696px"
                settings={{
                  pagination: false,
                  fullscreen: false,
                  changeHeight: false,
                  resetGrid: false,
                  saveGrid: false,
                  exportBtn: false
                }}
              />
            </Col>
          </>
        )}
      </Row>
    </div>
  );
};

export default PlannerDetails;
